/* global atlas */
import { ensureAzureMapsSdk } from "./azure-maps-sdk";
import {
  MAP_BASE_STYLE,
  MAP_BASE_STYLE_OVERRIDES,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  clampZoom,
} from "./map-controller-utils";
import { buildBultoLocationTooltipHtml } from "./bulto-location-map-tooltip";

export function createBultoLocationMapController(options) {
  const runtimeOptions = options && typeof options === "object" ? options : {};
  const mapElement =
    runtimeOptions.mapElement ||
    (runtimeOptions.rootElement?.querySelector
      ? runtimeOptions.rootElement.querySelector("#bulto-location-map")
      : document.getElementById("bulto-location-map"));
  const tooltipHostElement = (mapElement && mapElement.parentElement) || mapElement;
  const subscriptionKey = String(runtimeOptions.initialSubscriptionKey || "").trim();
  const initialStyle = String(runtimeOptions.initialStyle || MAP_BASE_STYLE).trim() || MAP_BASE_STYLE;

  const DEFAULT_CENTER = [-3.7038, 40.4168];
  const DEFAULT_ZOOM = 11.5;
  const DEFAULT_PADDING = 72;

  const state = {
    map: null,
    mapReady: null,
    destroyed: false,
    trailSource: null,
    historyMarkers: [],
    currentMarkers: [],
    hoverTooltip: null,
    lastPositions: [],
    currentHistories: [],
    displayMode: "current",
    resizeObserver: null,
    resizeTimer: null,
  };

  function ensureMap() {
    if (!mapElement) {
      return Promise.reject(new Error("No se encontro el contenedor del mapa de ubicacion."));
    }
    if (!subscriptionKey) {
      return Promise.reject(new Error("Falta la Azure Maps key."));
    }
    if (state.map) {
      return state.mapReady;
    }

    return ensureAzureMapsSdk().then(() => {
      if (state.map) return state.mapReady;

      state.destroyed = false;
      state.map = new atlas.Map(mapElement, {
        authOptions: { authType: "subscriptionKey", subscriptionKey },
        center: DEFAULT_CENTER,
        style: initialStyle,
        styleOverrides: MAP_BASE_STYLE_OVERRIDES,
        view: "Auto",
        zoom: DEFAULT_ZOOM,
        minZoom: MAP_MIN_ZOOM,
        maxZoom: MAP_MAX_ZOOM,
      });
      const mapInstance = state.map;

      state.mapReady = new Promise((resolve) => {
        mapInstance.events.add("ready", () => {
          if (state.destroyed || state.map !== mapInstance) {
            resolve();
            return;
          }

          state.trailSource = new atlas.source.DataSource();
          mapInstance.sources.add(state.trailSource);

          mapInstance.layers.add(
            new atlas.layer.LineLayer(state.trailSource, "bulto-trail-line", {
              strokeColor: "#5e72e4",
              strokeWidth: 3,
              strokeDashArray: [2, 2],
              lineCap: "round",
              lineJoin: "round",
            })
          );

          observeResize();
          resolve();
        });
      });

      return state.mapReady;
    });
  }

  function observeResize() {
    if (!window.ResizeObserver || !mapElement) return;

    state.resizeObserver = new ResizeObserver(() => {
      clearTimeout(state.resizeTimer);
      state.resizeTimer = setTimeout(() => {
        if (state.destroyed || !state.map) return;
        state.map.resize();
      }, 160);
    });

    state.resizeObserver.observe(mapElement);
  }

  function ensureHoverTooltip() {
    if (state.hoverTooltip) {
      return state.hoverTooltip;
    }

    if (!tooltipHostElement || typeof tooltipHostElement.appendChild !== "function") {
      return null;
    }

    const tooltip = document.createElement("div");
    tooltip.className = "map-stop-tooltip";
    tooltip.style.display = "none";
    tooltipHostElement.appendChild(tooltip);
    state.hoverTooltip = tooltip;
    return tooltip;
  }

  function positionTooltipNearAnchor(tooltip, anchorRect, rootRect) {
    const GAP = 10;
    const PADDING = 8;

    tooltip.style.transform = "none";
    tooltip.style.left = "-9999px";
    tooltip.style.top = "-9999px";

    const tooltipW = tooltip.offsetWidth;
    const tooltipH = tooltip.offsetHeight;

    const anchorCenterX = anchorRect.left - rootRect.left + anchorRect.width / 2;
    const anchorTopY = anchorRect.top - rootRect.top;

    let left = anchorCenterX - tooltipW / 2;
    let top = anchorTopY - tooltipH - GAP;

    left = Math.max(PADDING, Math.min(left, rootRect.width - tooltipW - PADDING));

    if (top < PADDING) {
      top = anchorTopY + anchorRect.height + GAP;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  function showTooltip(anchorElement, items) {
    const tooltip = ensureHoverTooltip();
    if (
      !tooltip ||
      !anchorElement ||
      !tooltipHostElement ||
      typeof tooltipHostElement.getBoundingClientRect !== "function"
    ) {
      return;
    }

    const rootRect = tooltipHostElement.getBoundingClientRect();
    const anchorRect = anchorElement.getBoundingClientRect();
    tooltip.innerHTML = buildBultoLocationTooltipHtml(items);
    tooltip.style.display = "block";
    positionTooltipNearAnchor(tooltip, anchorRect, rootRect);
  }

  function hideTooltip() {
    if (!state.hoverTooltip) return;
    state.hoverTooltip.style.display = "none";
  }

  function clearMarkers() {
    if (state.map) {
      [...state.historyMarkers, ...state.currentMarkers].forEach((marker) => {
        try {
          state.map.markers.remove(marker);
        } catch (_error) {
          // El mapa puede estar desmontandose mientras limpiamos los marcadores.
        }
      });
    }
    state.historyMarkers = [];
    state.currentMarkers = [];
    hideTooltip();
  }

  function groupEntriesByPosition(entries) {
    const groups = new Map();
    for (const entry of entries) {
      const key = `${entry.position[0].toFixed(5)},${entry.position[1].toFixed(5)}`;
      if (!groups.has(key)) {
        groups.set(key, { position: entry.position, items: [] });
      }
      groups.get(key).items.push({ barcode: entry.barcode, movement: entry.movement });
    }
    return Array.from(groups.values());
  }

  function buildCountBadge(group) {
    return group.items.length > 1
      ? `<span class="bulto-location-marker-count">${group.items.length}</span>`
      : "";
  }

  function createHistoryMarker(group) {
    const markerElement = document.createElement("div");
    markerElement.className = "bulto-location-marker-history";
    markerElement.innerHTML =
      '<i class="fa fa-box" aria-hidden="true"></i>' + buildCountBadge(group);

    markerElement.addEventListener("mouseenter", () => {
      showTooltip(markerElement, group.items);
    });
    markerElement.addEventListener("mouseleave", hideTooltip);

    const marker = new atlas.HtmlMarker({ htmlContent: markerElement, position: group.position });
    state.map.markers.add(marker);
    return marker;
  }

  function createCurrentMarker(group) {
    const markerElement = document.createElement("div");
    markerElement.className = "bulto-location-marker-current";
    markerElement.innerHTML =
      '<span class="bulto-location-marker-pulse"></span>' +
      '<span class="bulto-location-marker-core"><i class="fa fa-box" aria-hidden="true"></i></span>' +
      buildCountBadge(group);

    markerElement.addEventListener("mouseenter", () => {
      showTooltip(markerElement, group.items);
    });
    markerElement.addEventListener("mouseleave", hideTooltip);

    const marker = new atlas.HtmlMarker({ htmlContent: markerElement, position: group.position });
    state.map.markers.add(marker);
    state.currentMarkers.push(marker);
  }

  function toPosition(address) {
    const lat = Number.parseFloat(address?.coordenates?.lat);
    const lng = Number.parseFloat(address?.coordenates?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return [lng, lat];
  }

  function collapseConsecutiveSamePosition(entries) {
    const collapsed = [];
    for (const entry of entries) {
      const previous = collapsed[collapsed.length - 1];
      if (
        previous &&
        previous.position[0].toFixed(5) === entry.position[0].toFixed(5) &&
        previous.position[1].toFixed(5) === entry.position[1].toFixed(5)
      ) {
        collapsed[collapsed.length - 1] = entry;
        continue;
      }
      collapsed.push(entry);
    }
    return collapsed;
  }

  function fitToPositions(positions) {
    if (!state.map || !positions.length || !window.atlas?.data?.BoundingBox?.fromPositions) {
      return;
    }
    const bounds = atlas.data.BoundingBox.fromPositions(positions);
    state.map.setCamera({ bounds, padding: DEFAULT_PADDING, maxZoom: 15 });
  }

  function focusPosition(position) {
    if (!state.map || !position) return;
    state.map.setCamera({
      center: position,
      duration: 500,
      type: "ease",
      zoom: Math.max(Number(state.map.getCamera().zoom || DEFAULT_ZOOM), 14),
    });
  }

  async function renderCurrentHistories() {
    await ensureMap();
    if (!state.map || state.destroyed) return;

    clearMarkers();
    if (state.trailSource) state.trailSource.clear();

    const historyEntries = [];
    const currentEntries = [];
    const visiblePositions = [];

    for (const history of state.currentHistories) {
      const barcode = history?.barcode || "";
      const movements = Array.isArray(history?.movements) ? history.movements : [];
      const entries = collapseConsecutiveSamePosition(
        movements
          .map((movement) => ({ movement, position: toPosition(movement.address), barcode }))
          .filter((entry) => entry.position)
      );

      if (!entries.length) continue;

      const lastEntry = entries[entries.length - 1];

      if (state.displayMode === "trail") {
        if (entries.length > 1) {
          state.trailSource.add(new atlas.data.Feature(
            new atlas.data.LineString(entries.map((entry) => entry.position))
          ));
        }
        entries.slice(0, -1).forEach((entry) => {
          historyEntries.push(entry);
          visiblePositions.push(entry.position);
        });
      }

      currentEntries.push(lastEntry);
      visiblePositions.push(lastEntry.position);
    }

    groupEntriesByPosition(historyEntries).forEach((group) => {
      state.historyMarkers.push(createHistoryMarker(group));
    });

    groupEntriesByPosition(currentEntries).forEach((group) => {
      createCurrentMarker(group);
    });

    state.lastPositions = visiblePositions;
    if (!visiblePositions.length) return;

    if (visiblePositions.length === 1) {
      focusPosition(visiblePositions[0]);
      return;
    }
    fitToPositions(visiblePositions);
  }

  function setHistories(histories) {
    state.currentHistories = Array.isArray(histories) ? histories : [];
    return renderCurrentHistories();
  }

  function setDisplayMode(mode) {
    const nextMode = mode === "trail" ? "trail" : "current";
    if (nextMode === state.displayMode) return;
    state.displayMode = nextMode;
    renderCurrentHistories();
  }

  function zoomIn() {
    if (!state.map) return;
    const nextZoom = clampZoom(Number(state.map.getCamera().zoom || DEFAULT_ZOOM) + 1, DEFAULT_ZOOM);
    state.map.setCamera({ zoom: nextZoom });
  }

  function zoomOut() {
    if (!state.map) return;
    const nextZoom = clampZoom(Number(state.map.getCamera().zoom || DEFAULT_ZOOM) - 1, DEFAULT_ZOOM);
    state.map.setCamera({ zoom: nextZoom });
  }

  function fitMap() {
    if (state.lastPositions.length === 1) {
      focusPosition(state.lastPositions[0]);
      return;
    }
    fitToPositions(state.lastPositions);
  }

  function setStyle(style) {
    if (!state.map || !style || state.destroyed) return;
    try {
      state.map.setStyle({ style });
    } catch (_error) {
      // Ignoramos cambios de estilo tardios cuando la vista esta saliendo.
    }
  }

  function destroy() {
    state.destroyed = true;
    clearTimeout(state.resizeTimer);
    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
      state.resizeObserver = null;
    }
    clearMarkers();
    if (state.hoverTooltip?.parentElement) {
      state.hoverTooltip.parentElement.removeChild(state.hoverTooltip);
    }
    state.hoverTooltip = null;
    state.trailSource = null;
    state.lastPositions = [];
    state.currentHistories = [];
    state.displayMode = "current";
    if (state.map) {
      try {
        state.map.dispose();
      } catch (_error) {
        // Si el SDK ya esta liberando recursos, evitamos propagar errores de dispose.
      }
    }
    state.map = null;
    state.mapReady = null;
  }

  return {
    destroy,
    fitMap,
    setDisplayMode,
    setHistories,
    setStyle,
    zoomIn,
    zoomOut,
  };
}
